/**
 * AdminCabinCurriculum — Kabin müfredatı yönetimi (modül / ünite / ders).
 * Route: /admin/cabin-curriculum · Roles: admin, superadmin
 *
 * Modül listesi (accordion) → ünite → ders ağacı. Her seviyede ekle / düzenle / sil.
 * Ders içeriği (phase editörü) bu sürümde yok — yeni ders minimal bir intro phase
 * ile oluşturulur, içerik ileride ayrı bir editörle eklenecek.
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/admin/AdminLayout';
import { PageHeader, ConfirmDialog, LoadingState } from '../components/admin';
import BilingualTextField from '../components/admin/BilingualTextField';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import {
  useAdminCabinModules,
  useAdminCabinModuleDetail,
  useCreateModule,
  useUpdateModule,
  useDeleteModule,
  useCreateUnit,
  useUpdateUnit,
  useDeleteUnit,
  useCreateLesson,
  useUpdateLesson,
  useDeleteLesson,
  fetchAdminLessonRaw,
} from '../api/admin/cabinCurriculum';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Plus, Edit, Trash2, ChevronDown, ChevronRight, BookOpen, Layers, GraduationCap, FileText,
} from 'lucide-react';

const GRADIENTS = [
  'from-blue-500 to-indigo-600',
  'from-emerald-500 to-teal-600',
  'from-purple-500 to-pink-600',
  'from-amber-500 to-orange-600',
  'from-rose-500 to-red-600',
  'from-cyan-500 to-sky-600',
  'from-violet-500 to-fuchsia-600',
  'from-lime-500 to-green-600',
];

// Build a minimal valid intro phase so the lesson passes backend validation.
function buildIntroPhase(titleTr, titleEn) {
  return {
    type: 'intro',
    title: { tr: 'Giriş', en: 'Intro' },
    content: {
      text: { tr: titleTr || 'İçerik yakında eklenecek.', en: titleEn || 'Content coming soon.' },
      key_takeaways: [],
    },
    order: 1,
    xp_in_phase: 0,
    estimated_seconds: 30,
  };
}

const EMPTY = {
  // module
  slug: '', titleTr: '', titleEn: '', icon_emoji: '✈️',
  color_gradient: GRADIENTS[0], order: 1, estimated_hours: 0, is_active: true,
  // unit/lesson shared
  code: '', estimated_minutes: 0,
  // lesson
  xp_reward: 20, miles_reward: 10, difficulty: 1, duration_min: 5,
};

export default function AdminCabinCurriculum() {
  const { lang } = useLanguage();
  const toast = useToast();

  const { data: modules = [], isLoading } = useAdminCabinModules(lang);
  const [expandedSlug, setExpandedSlug] = useState(null);

  // modal: { entity:'module'|'unit'|'lesson', mode:'create'|'edit', form, id, parentId }
  const [modal, setModal] = useState(null);
  const [confirm, setConfirm] = useState(null); // { entity, id, label }

  const createModule = useCreateModule();
  const updateModule = useUpdateModule();
  const deleteModule = useDeleteModule();
  const createUnit = useCreateUnit();
  const updateUnit = useUpdateUnit();
  const deleteUnit = useDeleteUnit();
  const createLesson = useCreateLesson();
  const updateLesson = useUpdateLesson();
  const deleteLesson = useDeleteLesson();

  const setField = (name, value) => setModal((m) => ({ ...m, form: { ...m.form, [name]: value } }));

  const openCreate = (entity, parentId = null, suggestedOrder = 1) =>
    setModal({ entity, mode: 'create', id: null, parentId, form: { ...EMPTY, order: suggestedOrder } });

  const openEditModule = (mod) =>
    setModal({
      entity: 'module', mode: 'edit', id: mod.module_id, parentId: null,
      form: {
        ...EMPTY,
        slug: mod.slug ?? '',
        titleTr: typeof mod.title === 'string' ? mod.title : (mod.title?.tr ?? ''),
        titleEn: typeof mod.title === 'string' ? mod.title : (mod.title?.en ?? ''),
        icon_emoji: mod.icon_emoji ?? '✈️',
        color_gradient: mod.color_gradient ?? GRADIENTS[0],
        order: mod.order ?? 1,
        estimated_hours: mod.estimated_hours ?? 0,
        is_active: mod.is_active ?? true,
      },
    });

  const openEditUnit = (unit) =>
    setModal({
      entity: 'unit', mode: 'edit', id: unit.unit_id, parentId: null,
      form: {
        ...EMPTY,
        code: unit.code ?? '',
        titleTr: typeof unit.title === 'string' ? unit.title : (unit.title?.tr ?? ''),
        titleEn: typeof unit.title === 'string' ? unit.title : (unit.title?.en ?? ''),
        order: unit.order ?? 1,
        estimated_minutes: unit.estimated_minutes ?? 0,
      },
    });

  const openEditLesson = async (lesson) => {
    // Public endpoints resolve title to a single language string,
    // so we always fetch the raw admin endpoint to get the bilingual {tr, en} object.
    let rawTitle = lesson.title;
    try {
      const raw = await fetchAdminLessonRaw(lesson.lesson_id);
      rawTitle = raw.title ?? lesson.title;
    } catch {
      // Fallback: use whatever title is available from the list response.
    }
    const titleTr = typeof rawTitle === 'string' ? rawTitle : (rawTitle?.tr ?? '');
    const titleEn = typeof rawTitle === 'object' && rawTitle !== null ? (rawTitle?.en ?? '') : '';
    setModal({
      entity: 'lesson', mode: 'edit', id: lesson.lesson_id, parentId: null,
      form: {
        ...EMPTY,
        code: lesson.code ?? '',
        titleTr,
        titleEn,
        order: lesson.order ?? 1,
        xp_reward: lesson.xp_reward ?? 20,
        miles_reward: lesson.miles_reward ?? 10,
        difficulty: lesson.difficulty ?? 1,
        duration_min: lesson.duration_min ?? 5,
      },
    });
  };

  const closeModal = () => setModal(null);

  const handleSubmit = async () => {
    if (!modal) return;
    const f = modal.form;
    const title = { tr: f.titleTr.trim(), en: (f.titleEn.trim() || f.titleTr.trim()) };
    if (!title.tr) {
      toast.error(lang === 'tr' ? 'Başlık (TR) zorunlu' : 'Title (TR) is required');
      return;
    }
    try {
      if (modal.entity === 'module') {
        const payload = {
          slug: f.slug.trim(), title, icon_emoji: f.icon_emoji || '✈️',
          color_gradient: f.color_gradient, estimated_hours: Number(f.estimated_hours) || 0,
          order: Number(f.order) || 1, is_active: f.is_active,
        };
        if (modal.mode === 'create') await createModule.mutateAsync(payload);
        else await updateModule.mutateAsync({ id: modal.id, data: payload });
      } else if (modal.entity === 'unit') {
        const payload = {
          module_id: modal.parentId, code: f.code.trim(), title,
          estimated_minutes: Number(f.estimated_minutes) || 0, order: Number(f.order) || 1,
        };
        if (modal.mode === 'create') await createUnit.mutateAsync(payload);
        else await updateUnit.mutateAsync({ id: modal.id, data: { title, code: f.code.trim(), estimated_minutes: Number(f.estimated_minutes) || 0, order: Number(f.order) || 1 } });
      } else if (modal.entity === 'lesson') {
        const base = {
          code: f.code.trim(), title, duration_min: Number(f.duration_min) || 5,
          xp_reward: Number(f.xp_reward) || 0, miles_reward: Number(f.miles_reward) || 0,
          difficulty: Number(f.difficulty) || 1, order: Number(f.order) || 1,
        };
        if (modal.mode === 'create') {
          await createLesson.mutateAsync({ ...base, unit_id: modal.parentId, phases: [buildIntroPhase(title.tr, title.en)] });
        } else {
          await updateLesson.mutateAsync({ id: modal.id, data: base });
        }
      }
      toast.success(lang === 'tr' ? 'Kaydedildi' : 'Saved');
      closeModal();
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
    }
  };

  const handleDelete = async () => {
    if (!confirm) return;
    try {
      if (confirm.entity === 'module') await deleteModule.mutateAsync(confirm.id);
      else if (confirm.entity === 'unit') await deleteUnit.mutateAsync(confirm.id);
      else await deleteLesson.mutateAsync(confirm.id);
      toast.success(lang === 'tr' ? 'Silindi' : 'Deleted');
      setConfirm(null);
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang));
      setConfirm(null);
    }
  };

  const modalTitle = () => {
    if (!modal) return '';
    const map = {
      module: lang === 'tr' ? 'Modül' : 'Module',
      unit: lang === 'tr' ? 'Ünite' : 'Unit',
      lesson: lang === 'tr' ? 'Ders' : 'Lesson',
    };
    const action = modal.mode === 'create' ? (lang === 'tr' ? 'Ekle' : 'Add') : (lang === 'tr' ? 'Düzenle' : 'Edit');
    return `${map[modal.entity]} ${action}`;
  };

  return (
    <AdminLayout>
      <PageHeader
        breadcrumbs={[
          { label: 'Admin', href: '/admin' },
          { label: lang === 'tr' ? 'Kabin Akademisi' : 'Cabin Academy', href: '/admin/cabin-academy' },
          { label: lang === 'tr' ? 'Müfredat Yönetimi' : 'Curriculum Management' },
        ]}
        title={lang === 'tr' ? 'Müfredat Yönetimi' : 'Curriculum Management'}
        subtitle={lang === 'tr' ? 'Kabin memurluğu modül, ünite ve derslerini yönet' : 'Manage cabin crew modules, units and lessons'}
        actions={
          <Button onClick={() => openCreate('module', null, (modules?.length ?? 0) + 1)}>
            <Plus size={16} className="mr-1" /> {lang === 'tr' ? 'Yeni Modül' : 'New Module'}
          </Button>
        }
      />

      {isLoading ? (
        <LoadingState />
      ) : (
        <div className="flex flex-col gap-3">
          {modules.map((mod) => (
            <ModuleRow
              key={mod.module_id}
              mod={mod}
              lang={lang}
              expanded={expandedSlug === mod.slug}
              onToggle={() => setExpandedSlug(expandedSlug === mod.slug ? null : mod.slug)}
              onEdit={() => openEditModule(mod)}
              onDelete={() => setConfirm({ entity: 'module', id: mod.module_id, label: typeof mod.title === 'string' ? mod.title : mod.title?.tr })}
              onAddUnit={(count) => openCreate('unit', mod.module_id, count + 1)}
              onEditUnit={openEditUnit}
              onDeleteUnit={(u) => setConfirm({ entity: 'unit', id: u.unit_id, label: typeof u.title === 'string' ? u.title : u.title?.tr })}
              onAddLesson={(unitId, count) => openCreate('lesson', unitId, count + 1)}
              onEditLesson={openEditLesson}
              onDeleteLesson={(l) => setConfirm({ entity: 'lesson', id: l.lesson_id, label: typeof l.title === 'string' ? l.title : l.title?.tr })}
            />
          ))}
          {modules.length === 0 && (
            <div className="text-center py-12 text-gray-500 text-sm">
              {lang === 'tr' ? 'Henüz modül yok. "Yeni Modül" ile başla.' : 'No modules yet. Start with "New Module".'}
            </div>
          )}
        </div>
      )}

      {/* Entity modal */}
      <Dialog open={!!modal} onOpenChange={(o) => !o && closeModal()}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{modalTitle()}</DialogTitle>
          </DialogHeader>

          {modal && (
            <div className="flex flex-col gap-4 py-2">
              {/* slug (module) / code (unit, lesson) */}
              {modal.entity === 'module' ? (
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-600">Slug</Label>
                  <Input value={modal.form.slug} onChange={(e) => setField('slug', e.target.value)} placeholder="kabin-memurluguna-giris" />
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold text-gray-600">Kod</Label>
                  <Input
                    value={modal.form.code}
                    onChange={(e) => setField('code', e.target.value)}
                    placeholder={modal.entity === 'unit' ? 'CM-M1-U1' : 'CM-M1-U1-L1'}
                  />
                </div>
              )}

              <BilingualTextField
                labelTr={lang === 'tr' ? 'Başlık (TR)' : 'Title (TR)'}
                labelEn={lang === 'tr' ? 'Başlık (EN)' : 'Title (EN)'}
                nameTr="titleTr"
                nameEn="titleEn"
                valueTr={modal.form.titleTr}
                valueEn={modal.form.titleEn}
                onChange={setField}
                required
              />

              {modal.entity === 'module' && (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-gray-600">Emoji</Label>
                      <Input value={modal.form.icon_emoji} onChange={(e) => setField('icon_emoji', e.target.value)} maxLength={4} />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-gray-600">{lang === 'tr' ? 'Sıra' : 'Order'}</Label>
                      <Input type="number" min={1} value={modal.form.order} onChange={(e) => setField('order', e.target.value)} />
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-gray-600">{lang === 'tr' ? 'Renk Gradyanı' : 'Color Gradient'}</Label>
                    <div className="flex flex-wrap gap-2">
                      {GRADIENTS.map((g) => (
                        <button
                          key={g}
                          type="button"
                          onClick={() => setField('color_gradient', g)}
                          className={`h-8 w-12 rounded-lg bg-gradient-to-br ${g} ${modal.form.color_gradient === g ? 'ring-2 ring-offset-2 ring-gray-800' : ''}`}
                          aria-label={g}
                        />
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3 items-end">
                    <div className="space-y-1.5">
                      <Label className="text-xs font-semibold text-gray-600">{lang === 'tr' ? 'Tahmini Saat' : 'Est. Hours'}</Label>
                      <Input type="number" min={0} step="0.5" value={modal.form.estimated_hours} onChange={(e) => setField('estimated_hours', e.target.value)} />
                    </div>
                    <div className="flex items-center gap-2 pb-2">
                      <Switch checked={modal.form.is_active} onCheckedChange={(v) => setField('is_active', v)} />
                      <Label className="text-xs font-semibold text-gray-600">{lang === 'tr' ? 'Aktif' : 'Active'}</Label>
                    </div>
                  </div>
                </>
              )}

              {modal.entity === 'unit' && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-gray-600">{lang === 'tr' ? 'Sıra' : 'Order'}</Label>
                    <Input type="number" min={1} value={modal.form.order} onChange={(e) => setField('order', e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-gray-600">{lang === 'tr' ? 'Tahmini Dakika' : 'Est. Minutes'}</Label>
                    <Input type="number" min={0} value={modal.form.estimated_minutes} onChange={(e) => setField('estimated_minutes', e.target.value)} />
                  </div>
                </div>
              )}

              {modal.entity === 'lesson' && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-gray-600">{lang === 'tr' ? 'Sıra' : 'Order'}</Label>
                    <Input type="number" min={1} value={modal.form.order} onChange={(e) => setField('order', e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-gray-600">{lang === 'tr' ? 'Süre (dk)' : 'Duration (min)'}</Label>
                    <Input type="number" min={1} value={modal.form.duration_min} onChange={(e) => setField('duration_min', e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-gray-600">XP</Label>
                    <Input type="number" min={0} value={modal.form.xp_reward} onChange={(e) => setField('xp_reward', e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-gray-600">{lang === 'tr' ? 'Mil' : 'Miles'}</Label>
                    <Input type="number" min={0} value={modal.form.miles_reward} onChange={(e) => setField('miles_reward', e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-semibold text-gray-600">{lang === 'tr' ? 'Zorluk (1-5)' : 'Difficulty (1-5)'}</Label>
                    <Input type="number" min={1} max={5} value={modal.form.difficulty} onChange={(e) => setField('difficulty', e.target.value)} />
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={closeModal}>{lang === 'tr' ? 'İptal' : 'Cancel'}</Button>
            <Button onClick={handleSubmit}>{lang === 'tr' ? 'Kaydet' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={lang === 'tr' ? 'Silme onayı' : 'Confirm delete'}
        description={
          confirm
            ? (lang === 'tr' ? `"${confirm.label}" silinsin mi? Bu işlem geri alınamaz.` : `Delete "${confirm.label}"? This cannot be undone.`)
            : ''
        }
        confirmLabel={lang === 'tr' ? 'Sil' : 'Delete'}
        onConfirm={handleDelete}
        variant="danger"
      />
    </AdminLayout>
  );
}

// ─── Module accordion row ────────────────────────────────────────────────────────
function ModuleRow({
  mod, lang, expanded, onToggle, onEdit, onDelete,
  onAddUnit, onEditUnit, onDeleteUnit, onAddLesson, onEditLesson, onDeleteLesson,
}) {
  const navigate = useNavigate();
  const { data: detail, isLoading } = useAdminCabinModuleDetail(expanded ? mod.slug : null, lang);
  const units = detail?.units ?? [];
  const title = typeof mod.title === 'string' ? mod.title : mod.title?.tr ?? '';

  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
      {/* Module header */}
      <div className="flex items-center gap-3 p-3">
        <button onClick={onToggle} className="flex items-center gap-2 flex-1 min-w-0 text-left">
          {expanded ? <ChevronDown size={18} className="text-gray-400 shrink-0" /> : <ChevronRight size={18} className="text-gray-400 shrink-0" />}
          <span className="text-2xl shrink-0">{mod.icon_emoji ?? '✈️'}</span>
          <div className="min-w-0">
            <p className="font-bold text-gray-900 truncate">{title}</p>
            <p className="text-xs text-gray-500">
              {lang === 'tr' ? 'Sıra' : 'Order'} {mod.order} · {mod.total_units ?? units.length} {lang === 'tr' ? 'ünite' : 'units'}
            </p>
          </div>
        </button>
        <Badge variant={mod.is_active === false ? 'secondary' : 'default'} className="shrink-0">
          {mod.is_active === false ? (lang === 'tr' ? 'Pasif' : 'Inactive') : (lang === 'tr' ? 'Aktif' : 'Active')}
        </Badge>
        <Button variant="ghost" size="icon" onClick={onEdit} aria-label="edit"><Edit size={16} /></Button>
        <Button variant="ghost" size="icon" onClick={onDelete} aria-label="delete"><Trash2 size={16} className="text-red-500" /></Button>
      </div>

      {/* Units + lessons */}
      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50/50 p-3 pl-8 flex flex-col gap-2">
          {isLoading ? (
            <p className="text-sm text-gray-400 py-2">{lang === 'tr' ? 'Yükleniyor…' : 'Loading…'}</p>
          ) : (
            <>
              {units.map((u) => (
                <div key={u.unit_id} className="rounded-lg border border-gray-200 bg-white">
                  <div className="flex items-center gap-2 p-2.5">
                    <Layers size={15} className="text-indigo-500 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 truncate">
                        {typeof u.title === 'string' ? u.title : u.title?.tr}
                      </p>
                      <p className="text-[11px] text-gray-400">{u.code} · {(u.lessons ?? []).length} {lang === 'tr' ? 'ders' : 'lessons'}</p>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => onEditUnit(u)} aria-label="edit-unit"><Edit size={14} /></Button>
                    <Button variant="ghost" size="icon" onClick={() => onDeleteUnit(u)} aria-label="delete-unit"><Trash2 size={14} className="text-red-500" /></Button>
                  </div>
                  {/* Lessons */}
                  <div className="border-t border-gray-100 pl-7 pr-2 py-1.5 flex flex-col gap-1">
                    {(u.lessons ?? []).map((l) => (
                      <div key={l.lesson_id} className="flex items-center gap-2 py-1">
                        <GraduationCap size={13} className="text-emerald-500 shrink-0" />
                        <span className="text-[13px] text-gray-700 flex-1 min-w-0 truncate">
                          {typeof l.title === 'string' ? l.title : l.title?.tr}
                        </span>
                        <span className="text-[11px] text-gray-400 shrink-0">{l.code} · {l.xp_reward ?? 0} XP</span>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => navigate(`/admin/cabin-curriculum/lessons/${l.lesson_id}`)} aria-label="edit-content" title={lang === 'tr' ? 'İçeriği Düzenle' : 'Edit content'}><FileText size={13} className="text-sky-600" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onEditLesson(l)} aria-label="edit-lesson"><Edit size={13} /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => onDeleteLesson(l)} aria-label="delete-lesson"><Trash2 size={13} className="text-red-500" /></Button>
                      </div>
                    ))}
                    <button
                      onClick={() => onAddLesson(u.unit_id, (u.lessons ?? []).length)}
                      className="flex items-center gap-1 text-[12px] font-semibold text-emerald-600 hover:underline py-1"
                    >
                      <Plus size={13} /> {lang === 'tr' ? 'Ders Ekle' : 'Add Lesson'}
                    </button>
                  </div>
                </div>
              ))}
              <button
                onClick={() => onAddUnit(units.length)}
                className="flex items-center gap-1 text-[13px] font-semibold text-indigo-600 hover:underline py-1.5"
              >
                <Plus size={14} /> {lang === 'tr' ? 'Ünite Ekle' : 'Add Unit'}
              </button>
              {units.length === 0 && (
                <p className="text-xs text-gray-400 flex items-center gap-1"><BookOpen size={13} /> {lang === 'tr' ? 'Bu modülde ünite yok.' : 'No units in this module.'}</p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
